import { onCleanup, onMount, type JSX } from 'solid-js';

export interface ReusableStylesheet {
  readonly key: string;
  readonly cssText: string;
  readonly sheet?: CSSStyleSheet;
}

type AdoptionMode = 'constructable' | 'fallback';

interface StylesheetOwnership {
  count: number;
  modes: Set<AdoptionMode>;
  /**
   * Constructable sheet currently on the root for this key.
   * First successful constructable adopt for a key owns the installed
   * instance; later same-key adopts only bump `count`.
   */
  installedSheet?: CSSStyleSheet;
}

interface ReleasedStylesheetOwnership {
  readonly stylesheet: ReusableStylesheet;
  readonly modes: ReadonlySet<AdoptionMode>;
  readonly installedSheet?: CSSStyleSheet;
}

const reusableStylesheetKeyProperty = '__trimbleReusableStylesheetKey';
const reusableStylesheetSignatureProperty =
  '__trimbleReusableStylesheetSignature';
const reusableStylesheetOwnershipProperty =
  '__trimbleReusableStylesheetOwnership';
const reusableStylesheetFallbackKeysProperty =
  '__trimbleReusableStylesheetFallbackKeys';

interface KeyedCSSStyleSheet extends CSSStyleSheet {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __trimbleReusableStylesheetKey?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __trimbleReusableStylesheetSignature?: string;
}

interface StyleRootWithOwnership {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __trimbleReusableStylesheetOwnership?: Map<string, StylesheetOwnership>;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __trimbleReusableStylesheetFallbackKeys?: Set<string>;
}

export function createReusableStylesheet(
  key: string,
  cssText: string
): ReusableStylesheet {
  const sheet = createConstructableStylesheet(cssText);

  if (sheet) {
    markStylesheet(sheet, key);
    return { key, cssText, sheet };
  }

  return { key, cssText };
}

export function adoptReusableStylesheets(
  root: Document | ShadowRoot,
  stylesheets: readonly ReusableStylesheet[]
): void {
  const adoptions = stylesheets.map((stylesheet) => ({
    stylesheet,
    modes: getAdoptionModes(root, stylesheet),
  }));

  trackReusableStylesheets(root, adoptions);

  const constructableStylesheets = adoptions.flatMap(({ stylesheet, modes }) =>
    modes.includes('constructable') && stylesheet.sheet
      ? [stylesheet.sheet]
      : []
  );

  if (constructableStylesheets.length > 0 && 'adoptedStyleSheets' in root) {
    const sheetsToAdd = collectConstructableSheetsToAdd(
      root.adoptedStyleSheets,
      constructableStylesheets
    );

    if (sheetsToAdd.length > 0) {
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, ...sheetsToAdd];
      recordInstalledSheets(root, sheetsToAdd);
    }
  }

  for (const { stylesheet, modes } of adoptions) {
    if (!modes.includes('fallback')) {
      continue;
    }

    appendFallbackStyle(root, stylesheet);
  }
}

export function releaseReusableStylesheets(
  root: Document | ShadowRoot,
  stylesheets: readonly ReusableStylesheet[]
): void {
  const releasedOwnerships = decrementStylesheetOwnership(root, stylesheets);

  if (releasedOwnerships.length === 0) {
    return;
  }

  if ('adoptedStyleSheets' in root) {
    const releasedConstructableStylesheetSet = new Set(
      releasedOwnerships.flatMap(({ stylesheet, modes, installedSheet }) => {
        if (!modes.has('constructable')) {
          return [];
        }

        return [stylesheet.sheet, installedSheet].filter(
          (sheet): sheet is CSSStyleSheet => sheet !== undefined
        );
      })
    );
    const releasedKeys = new Set(
      releasedOwnerships.flatMap(({ stylesheet, modes }) =>
        modes.has('constructable') ? [stylesheet.key] : []
      )
    );

    if (releasedConstructableStylesheetSet.size > 0 || releasedKeys.size > 0) {
      root.adoptedStyleSheets = root.adoptedStyleSheets.filter((stylesheet) => {
        if (releasedConstructableStylesheetSet.has(stylesheet)) {
          return false;
        }

        const stylesheetKey = getStylesheetKey(stylesheet);
        return stylesheetKey === undefined || !releasedKeys.has(stylesheetKey);
      });
    }
  }

  const fallbackKeys = getFallbackKeys(root);
  const host = getFallbackStyleHost(root);

  for (const { stylesheet, modes } of releasedOwnerships) {
    if (!modes.has('fallback')) {
      continue;
    }

    getFallbackStyleElement(host, stylesheet.key)?.remove();
    fallbackKeys.delete(stylesheet.key);
  }
}

export function AdoptReusableStylesheets(props: {
  readonly stylesheets: readonly ReusableStylesheet[];
}): JSX.Element {
  if (typeof document === 'undefined') {
    return null;
  }

  const marker = document.createElement('span');
  marker.style.display = 'none';

  onMount(() => {
    const root = marker.getRootNode();

    if (isStyleRoot(root)) {
      adoptReusableStylesheets(root, props.stylesheets);
      onCleanup(() => releaseReusableStylesheets(root, props.stylesheets));
    }
  });

  return marker;
}

function getAdoptionModes(
  root: Document | ShadowRoot,
  stylesheet: ReusableStylesheet
): readonly AdoptionMode[] {
  if (stylesheet.sheet && 'adoptedStyleSheets' in root) {
    return ['constructable'];
  }

  return ['fallback'];
}

function collectConstructableSheetsToAdd(
  adoptedStyleSheets: readonly CSSStyleSheet[],
  candidates: readonly CSSStyleSheet[]
): CSSStyleSheet[] {
  const adoptedKeys = new Set<string>();
  const adoptedSignatures = new Set<string>();

  for (const stylesheet of adoptedStyleSheets) {
    const key = getStylesheetKey(stylesheet);
    if (key !== undefined) {
      adoptedKeys.add(key);
    }

    const signature = getStylesheetSignature(stylesheet);
    if (signature !== undefined) {
      adoptedSignatures.add(signature);
    }
  }

  const sheetsToAdd: CSSStyleSheet[] = [];

  for (const stylesheet of candidates) {
    if (sheetsToAdd.includes(stylesheet)) {
      continue;
    }

    const key = getStylesheetKey(stylesheet);
    // Federated hosts often load a second copy of the same reusable
    // stylesheet. Keep the adopted instance so unmounting the guest cannot
    // strip icon-font CSS the host still owns.
    if (key !== undefined && adoptedKeys.has(key)) {
      continue;
    }

    const signature = getStylesheetSignature(stylesheet);
    if (signature !== undefined && adoptedSignatures.has(signature)) {
      continue;
    }

    sheetsToAdd.push(stylesheet);

    if (key !== undefined) {
      adoptedKeys.add(key);
    }

    if (signature !== undefined) {
      adoptedSignatures.add(signature);
    }
  }

  return sheetsToAdd;
}

function trackReusableStylesheets(
  root: Document | ShadowRoot,
  adoptions: readonly {
    readonly stylesheet: ReusableStylesheet;
    readonly modes: readonly AdoptionMode[];
  }[]
): void {
  const ownershipByStylesheet = getOwnershipByKey(root);

  for (const { stylesheet, modes } of adoptions) {
    let ownership = ownershipByStylesheet.get(stylesheet.key);

    if (!ownership) {
      ownership = { count: 0, modes: new Set() };
      ownershipByStylesheet.set(stylesheet.key, ownership);
    }

    ownership.count += 1;

    for (const mode of modes) {
      ownership.modes.add(mode);
    }
  }
}

function decrementStylesheetOwnership(
  root: Document | ShadowRoot,
  stylesheets: readonly ReusableStylesheet[]
): ReleasedStylesheetOwnership[] {
  const ownershipByStylesheet = getOwnershipByKey(root);
  const releasedOwnerships: ReleasedStylesheetOwnership[] = [];

  for (const stylesheet of stylesheets) {
    const ownership = ownershipByStylesheet.get(stylesheet.key);

    if (ownership === undefined) {
      continue;
    }

    if (ownership.count > 1) {
      ownership.count -= 1;
      continue;
    }

    ownershipByStylesheet.delete(stylesheet.key);
    releasedOwnerships.push({
      stylesheet,
      modes: ownership.modes,
      installedSheet: ownership.installedSheet,
    });
  }

  return releasedOwnerships;
}

function recordInstalledSheets(
  root: Document | ShadowRoot,
  sheets: readonly CSSStyleSheet[]
): void {
  const ownershipByKey = getOwnershipByKey(root);

  for (const sheet of sheets) {
    const key = getStylesheetKey(sheet);
    if (key === undefined) {
      continue;
    }

    const ownership = ownershipByKey.get(key);
    if (ownership && ownership.installedSheet === undefined) {
      ownership.installedSheet = sheet;
    }
  }
}

function createConstructableStylesheet(
  cssText: string
): CSSStyleSheet | undefined {
  if (typeof CSSStyleSheet === 'undefined' || cssText.includes('@import')) {
    return undefined;
  }

  const stylesheet = new CSSStyleSheet();

  try {
    stylesheet.replaceSync(cssText);
  } catch {
    return undefined;
  }

  return stylesheet;
}

function markStylesheet(stylesheet: CSSStyleSheet, key: string): void {
  const signature = getStylesheetSignature(stylesheet);

  try {
    Object.defineProperty(stylesheet, reusableStylesheetKeyProperty, {
      configurable: true,
      value: key,
    });

    if (signature !== undefined) {
      Object.defineProperty(stylesheet, reusableStylesheetSignatureProperty, {
        configurable: true,
        value: signature,
      });
    }
  } catch {
    (stylesheet as KeyedCSSStyleSheet)[reusableStylesheetKeyProperty] = key;

    if (signature !== undefined) {
      (stylesheet as KeyedCSSStyleSheet)[reusableStylesheetSignatureProperty] =
        signature;
    }
  }
}

function getStylesheetKey(stylesheet: CSSStyleSheet): string | undefined {
  return (stylesheet as KeyedCSSStyleSheet)[reusableStylesheetKeyProperty];
}

function getStylesheetSignature(stylesheet: CSSStyleSheet): string | undefined {
  const markedSignature = (stylesheet as KeyedCSSStyleSheet)[
    reusableStylesheetSignatureProperty
  ];

  if (markedSignature !== undefined) {
    return markedSignature;
  }

  try {
    const rules = Array.from(stylesheet.cssRules);
    const rulesText = rules.map((rule) => rule.cssText).join('\n');

    return `${String(rules.length)}:${rulesText}`;
  } catch {
    return undefined;
  }
}

function appendFallbackStyle(
  root: Document | ShadowRoot,
  stylesheet: ReusableStylesheet
): void {
  const adoptedKeys = getFallbackKeys(root);
  const host = getFallbackStyleHost(root);
  const existingStyle = getFallbackStyleElement(host, stylesheet.key);

  if (existingStyle) {
    host.appendChild(existingStyle);
    adoptedKeys.add(stylesheet.key);
    return;
  }

  if (adoptedKeys.has(stylesheet.key)) {
    return;
  }

  const style = document.createElement('style');
  style.dataset['trimbleReusableStylesheet'] = stylesheet.key;
  style.textContent = stylesheet.cssText;
  host.appendChild(style);
  adoptedKeys.add(stylesheet.key);
}

function getFallbackStyleElement(
  host: Node,
  key: string
): HTMLStyleElement | undefined {
  if (
    !(
      host instanceof Document ||
      host instanceof ShadowRoot ||
      host instanceof Element
    )
  ) {
    return undefined;
  }

  return (
    Array.from(
      host.querySelectorAll<HTMLStyleElement>(
        'style[data-trimble-reusable-stylesheet]'
      )
    ).find((style) => style.dataset['trimbleReusableStylesheet'] === key) ??
    undefined
  );
}

function getFallbackKeys(root: Document | ShadowRoot): Set<string> {
  return getRootState(
    root,
    reusableStylesheetFallbackKeysProperty,
    () => new Set()
  );
}

function getOwnershipByKey(
  root: Document | ShadowRoot
): Map<string, StylesheetOwnership> {
  return getRootState(
    root,
    reusableStylesheetOwnershipProperty,
    () => new Map()
  );
}

function getRootState<T>(
  root: Document | ShadowRoot,
  property:
    | typeof reusableStylesheetOwnershipProperty
    | typeof reusableStylesheetFallbackKeysProperty,
  create: () => T
): T {
  const keyedRoot = root as Document | ShadowRoot | StyleRootWithOwnership;
  const existing = (keyedRoot as StyleRootWithOwnership)[property] as
    | T
    | undefined;

  if (existing) {
    return existing;
  }

  const next = create();

  try {
    Object.defineProperty(root, property, {
      configurable: true,
      value: next,
    });
  } catch {
    (keyedRoot as StyleRootWithOwnership)[property] = next as never;
  }

  return next;
}

function getFallbackStyleHost(root: Document | ShadowRoot): Node {
  if ('head' in root) {
    return root.head;
  }

  return root;
}

function isStyleRoot(root: Node): root is Document | ShadowRoot {
  return root instanceof Document || root instanceof ShadowRoot;
}
